import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdge, FaEdit } from "react-icons/fa";
import Select from "react-select";

import edit_icon from "../../../assets/blue_edit_icon.svg";
import { useTheme } from "../../../context/ThemeContext";
import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import { selectStylesV2 } from "../../../styles/selectStyles";
import { editClassroomModalValidationSchema } from "../../../utils/validationSchema";
import Button from "../../Button";
import LoadingBtn from "../../LoadingBtn";
import Modal from "../../Modal";

const EditClassroomModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showSnack } = useSnack();
  const [isOpen, setIsOpen] = useState(false);
  const [grades, setGrades] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(classroom) {
      setSelectedClassroom(classroom);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const formik = useFormik({
    initialValues: {
      name: "",
      grade: null,
      section: "",
    },
    validationSchema: editClassroomModalValidationSchema(t),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = new FormData();
        payload.append("name", values.name);
        payload.append("teacher_id", selectedClassroom?.classroom?.teacher?.id);
        payload.append("school_grade_id", values.grade);
        payload.append("section", values.section);
        payload.append("_method", "PUT");

        const response = await classroomServices.editClassroom(selectedClassroom?.classroom?.id, payload);
        showSnack(response.message, "success");
        onSubmit(values);
        resetForm();
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        setIsSubmitting(false);
        showSnack(error, "error");
      }
    },
  });

  const getGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await schoolServices.getGradesBySchool();
      const formattedGrades = response.map((grade) => ({
        label: grade.name,
        value: grade.id,
      }));
      setGrades(formattedGrades);
      formik.setValues({
        name: selectedClassroom.classroom.name,
        grade: formattedGrades.find((g) => g.value === selectedClassroom.grade.id)?.value || null,
        section: selectedClassroom.classroom.section,
      });
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingGrades(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getGrades();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      header={
        <p className="flex items-center gap-2 text-blue-500">
          <FaEdit size={20} />
          <span>{t("edit_classroom")}</span>
        </p>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={() => setIsOpen(false)} label={t("cancel")} />
          <Button
            className="w-36"
            color="blue"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("save")}
          />
        </div>
      }
    >
      <div>
        <div className="mb-2">
          <div className="mb-1 flex items-end space-x-1">
            <label className="block text-sm font-normal text-gray-800 dark:text-white">
              {t("classroom_name")}
            </label>
            {formik.touched.name && (formik.errors.name === "Required" || formik.errors.name === "مطلوب") && (
              <span className="text-start text-xl leading-3 text-red-300">*</span>
            )}
          </div>
          <input
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={formik.values.name}
            onChange={formik.handleChange("name")}
          />
          {formik.touched.name && formik.errors.name !== "Required" && formik.errors.name !== "مطلوب" && (
            <p className="mt-1 text-start text-xs text-red-300">{formik.errors.name}</p>
          )}
        </div>
        <div>
          <div className="mb-1 flex items-end space-x-1">
            <label className="block text-sm font-normal text-gray-800 dark:text-white">{t("section")}</label>
            {formik.touched.section &&
              (formik.errors.section === "Required" || formik.errors.section === "مطلوب") && (
                <span className="text-start text-xl leading-3 text-red-300">*</span>
              )}
          </div>
          <input
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={formik.values.section}
            onChange={formik.handleChange("section")}
          />
          {formik.touched.section &&
            formik.errors.section !== "Required" &&
            formik.errors.section !== "مطلوب" && (
              <p className="mt-1 text-start text-xs text-red-300">{formik.errors.section}</p>
            )}
        </div>
        {/* Grade */}
        <div className="mb-2">
          <div className="mb-1 flex items-end space-x-1">
            <label className="block text-sm font-normal text-gray-800 dark:text-white">{t("grade")}</label>
            {formik.touched.grade &&
              (formik.errors.grade === "Required" || formik.errors.grade === "مطلوب") && (
                <span className="text-start text-xl leading-3 text-red-300">*</span>
              )}
          </div>
          <Dropdown
            pt={{
              wrapper: "text-sm",
              header: "bg-blue-200 p-2 rounded-t-md",
              input:
                "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
              root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
              panel:
                "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
              item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
            }}
            value={formik.values.grade}
            onChange={(e) => formik.setFieldValue("grade", e.value)}
            options={grades}
            optionLabel="label"
            optionValue="value"
            placeholder={t("select_grade")}
            className="w-full"
            loading={loadingGrades}
            filter
            emptyFilterMessage={t("no_results_found")}
          />

          {formik.touched.grade && formik.errors.grade !== "Required" && formik.errors.grade !== "مطلوب" && (
            <p className="mt-1 text-start text-xs text-red-300">{formik.errors.grade}</p>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default EditClassroomModal;
